import {
  type ContentType,
  CursorMode,
  ShowcaseContentListItemDto,
  SortDirection,
} from '@repo/api/web/models'

/**
 * Общая логика контентной ленты (новости, блог): курсорный листинг с
 * краулябельной пагинацией и фильтром по тегу из query (`?tag=`, точное
 * совпадение — контракт `Filter.Tag` showcase-API). Тег проносится в ссылку
 * следующей страницы (`carryParams`), canonical остаётся консервативным —
 * без query (см. useListingPagination)
 */
export async function useContentFeed(options: {
  type: ContentType
  /** Ключ useCursorListing — уникален на страницу ('news-list', 'blog-list') */
  listKey: string
}) {
  const route = useRoute()
  const { service } = useContent()
  const pagination = useListingPagination({ carryParams: ['tag'] })

  const tag = computed(() => {
    const value = String(route.query.tag ?? '').trim()
    return value || undefined
  })

  const {
    collection,
    pending,
    meta,
    hasAfter,
    nextPage,
    setSort,
    setFilter,
    load,
    query,
  } = await useCursorListing<any, ShowcaseContentListItemDto>({
    key: options.listKey,
    fetcher: q => service.list(q),
    transform: data => ShowcaseContentListItemDto.createFromCollection(data ?? []),
    initialQuery: {
      Limit: 12,
      'Filter.Type': options.type,
      'Filter.Tag': tag.value,
      SortField: 'publishAt',
      SortDirection: SortDirection.DESC,
      CursorMode: CursorMode.AFTER,
      ...pagination.cursorQuery.value,
    },
  })

  const nextHref = computed(() => pagination.nextPageHref(meta.value))

  function loadMore() {
    if (hasAfter.value && !pending.value) nextPage()
  }

  watch(tag, async (nextTag) => {
    setFilter({
      'Filter.Type': options.type,
      'Filter.Tag': nextTag,
      CursorField: undefined,
      CursorId: undefined,
      CursorMode: CursorMode.AFTER,
    })
    await load(true)
  })

  function applySort(sortField: string, sortDirection: SortDirection) {
    setSort(
      [{ id: sortField, desc: sortDirection === SortDirection.DESC }],
      true,
    )
  }

  /** Unique tags from loaded items (for chip suggestions) + active query tag */
  const availableTags = computed(() => {
    const set = new Set<string>()
    if (tag.value) set.add(tag.value)
    for (const item of collection.value) {
      for (const t of item.tags ?? []) {
        const trimmed = t.trim()
        if (trimmed) set.add(trimmed)
      }
    }
    return [...set].sort((a, b) => a.localeCompare(b, 'ru'))
  })

  return {
    collection,
    pending,
    nextHref,
    tag,
    loadMore,
    hasAfter,
    applySort,
    availableTags,
    query,
  }
}
